import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format, addDays } from 'date-fns';
import { cn } from '@/lib/utils';
import { 
  Ticket, 
  Plus, 
  Pencil, 
  Trash2, 
  Calendar, 
  CalendarIcon,
  Clock,
  DollarSign, 
  Percent, 
  Truck,
  Store,
  Search,
  KeyRound,
  LogIn,
  LogOut,
  AlertCircle,
  CheckCircle2,
  ToggleLeft,
  ToggleRight,
  Gift,
  ShoppingCart,
  ChevronDown,
  ChevronRight,
  X,
  Megaphone,
  Sparkles
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';

interface EatzyCouponEffect {
  type: 'flat' | 'percentage' | 'freedelivery' | 'freeitem';
  amount?: number;
  params?: { value: any };
}

interface EatzyCouponRule {
  type: 'weekdays' | 'validity' | 'servicetypes' | 'minorderamount' | 'restaurants' | 'menu' | 'item' | 'withtag' | 'maxuses' | 'period' | 'customer' | 'timerange' | 'onetime' | 'one_time';
  weekdays?: number[];
  from?: string;
  to?: string;
  servicetypes?: string[];
  amount?: number;
  restaurants?: number[];
  menu?: number[];
  item?: number[];
  tag?: string;
  maxuses?: number;
  params?: { value: any };
}

interface EatzyCoupon {
  id: number;
  business: number;
  code: string;
  name: string;
  hash: string;
  active: boolean;
  effects: EatzyCouponEffect[];
  rules: EatzyCouponRule[];
  uses?: number;
  created_at?: string;
  updated_at?: string;
}

interface EatzyCouponConfigItem {
  id: number;
  name: string;
  category?: number;
  restaurants?: number[];
}

interface EatzyCouponConfigCategory {
  id: number;
  name: string;
  restaurants?: number[];
}

interface EatzyCouponConfig {
  restaurants: { id: number; name: string }[];
  categories?: EatzyCouponConfigCategory[];
  items?: EatzyCouponConfigItem[];
  menus?: { id: number; name: string; restaurant_id: number }[];
}

interface LAMAuthResponse {
  success: boolean;
  accessToken?: string;
  refreshToken?: string;
  roles?: Record<string, string>;
  error?: string;
}

const WEEKDAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const SERVICE_TYPES = ['pickup', 'delivery'];

const DEFAULT_BUSINESS_ID = 5;

function getEffectLabel(effect: EatzyCouponEffect): string {
  const value = effect.params?.value ?? effect.amount;
  switch (effect.type) {
    case 'flat':
      return `$${(value || 0).toFixed?.(2) || value} off`;
    case 'percentage':
      return `${value || 0}% off`;
    case 'freedelivery':
      return 'Free delivery';
    case 'freeitem':
      return `Free item #${value}`;
    default:
      return effect.type;
  }
}

function getRuleLabel(rule: EatzyCouponRule): string {
  const params = rule.params?.value;
  switch (rule.type) {
    case 'weekdays': {
      const weekdays = params ?? rule.weekdays;
      return `Days: ${Array.isArray(weekdays) ? weekdays.map((d: number) => WEEKDAY_NAMES[d]).join(', ') : 'undefined'}`;
    }
    case 'validity':
    case 'period': {
      const from = params?.from ?? rule.from;
      const to = params?.to ?? rule.to;
      const formatTs = (ts: number | string) => {
        if (!ts && ts !== 0) return '?';
        const num = typeof ts === 'string' && /^\d+$/.test(ts) ? Number(ts) : ts;
        const d = typeof num === 'number' ? new Date(num * 1000) : new Date(num);
        if (isNaN(d.getTime())) return '?';
        return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
      };
      return `Valid: ${formatTs(from)} - ${formatTs(to)}`;
    }
    case 'servicetypes': {
      const services = params ?? rule.servicetypes;
      return `Services: ${Array.isArray(services) ? services.join(', ') : 'undefined'}`;
    }
    case 'minorderamount': {
      const minAmount = params ?? rule.amount;
      return `Min order: $${minAmount}`;
    }
    case 'restaurants': {
      const restaurants = params ?? rule.restaurants;
      return `Restaurants: ${Array.isArray(restaurants) ? restaurants.length : 'undefined'} selected`;
    }
    case 'menu': {
      const menuValue = params;
      if (menuValue && typeof menuValue === 'object' && menuValue.identifiers) {
        return `Required items: ${menuValue.identifiers.length} (min ${menuValue.minimum || 1})`;
      }
      return `Menu rule`;
    }
    case 'maxuses': {
      const maxUses = params ?? rule.maxuses;
      return `Max uses: ${maxUses}`;
    }
    default:
      return rule.type;
  }
}

interface CouponFormData {
  code: string;
  name: string;
  active: boolean;
  effectType: 'flat' | 'percentage' | 'freedelivery' | 'freeitems';
  effectAmount: number;
  freeItemIds: number[];
  validFrom: string;
  validTo: string;
  validFromTime: string;
  validToTime: string;
  minOrderAmount: number;
  serviceTypes: string[];
  weekdays: number[];
  singleUse: boolean;
  oncePerCustomer: boolean;
  newCustomersOnly: boolean;
  selectedRestaurants: number[];
  requiredItemIds: number[];
  requiredItemMinimum: number;
  isPromo: boolean;
  promoTitle: string;
  promoOffer: string;
  promoDescription: string;
}

const INITIAL_FORM_DATA: CouponFormData = {
  code: '',
  name: '',
  active: true,
  effectType: 'percentage',
  effectAmount: 10,
  freeItemIds: [],
  validFrom: '',
  validTo: '',
  validFromTime: '',
  validToTime: '',
  minOrderAmount: 0,
  serviceTypes: [],
  weekdays: [0, 1, 2, 3, 4, 5, 6],
  singleUse: false,
  oncePerCustomer: false,
  newCustomersOnly: false,
  selectedRestaurants: [],
  requiredItemIds: [],
  requiredItemMinimum: 1,
  isPromo: false,
  promoTitle: '',
  promoOffer: '',
  promoDescription: '',
};

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const hour = Math.floor(i / 2);
  const minute = i % 2 === 0 ? '00' : '30';
  const ampm = hour < 12 ? 'AM' : 'PM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return {
    value: `${hour.toString().padStart(2, '0')}:${minute}`,
    label: `${displayHour}:${minute} ${ampm}`,
  };
});

function CategoryItemPicker({ 
  items, 
  categories,
  selectedIds, 
  onChange, 
  label, 
  placeholder,
  restaurantId,
}: { 
  items: EatzyCouponConfigItem[]; 
  categories: EatzyCouponConfigCategory[];
  selectedIds: number[]; 
  onChange: (ids: number[]) => void; 
  label: string;
  placeholder: string;
  restaurantId: number;
}) {
  const [search, setSearch] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
  
  const restaurantItems = useMemo(() => 
    items.filter(item => item.restaurants?.includes(restaurantId)),
    [items, restaurantId]
  );

  const restaurantCategories = useMemo(() => {
    const catIds = new Set(restaurantItems.map(item => item.category).filter(Boolean));
    return categories.filter(cat => catIds.has(cat.id));
  }, [categories, restaurantItems]);

  const itemsByCategory = useMemo(() => {
    const map = new Map<number, EatzyCouponConfigItem[]>();
    for (const item of restaurantItems) {
      if (item.category) {
        const existing = map.get(item.category) || [];
        existing.push(item);
        map.set(item.category, existing);
      }
    }
    return map;
  }, [restaurantItems]);

  const filteredCategories = useMemo(() => {
    if (!search) return restaurantCategories;
    const q = search.toLowerCase();
    return restaurantCategories.filter(cat => {
      if (cat.name.toLowerCase().includes(q)) return true;
      const catItems = itemsByCategory.get(cat.id) || [];
      return catItems.some(item => item.name.toLowerCase().includes(q));
    });
  }, [restaurantCategories, search, itemsByCategory]);
  
  const selectedItems = useMemo(() => 
    restaurantItems.filter(item => selectedIds.includes(item.id)),
    [restaurantItems, selectedIds]
  );

  const toggleCategory = (catId: number) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      return next;
    });
  };

  const isCategoryFullySelected = (catId: number) => {
    const catItems = itemsByCategory.get(catId) || [];
    return catItems.length > 0 && catItems.every(item => selectedIds.includes(item.id));
  };

  const isCategoryPartiallySelected = (catId: number) => {
    const catItems = itemsByCategory.get(catId) || [];
    return catItems.some(item => selectedIds.includes(item.id)) && !isCategoryFullySelected(catId);
  };

  const toggleCategorySelection = (catId: number) => {
    const catItems = itemsByCategory.get(catId) || [];
    const catItemIds = catItems.map(i => i.id);
    if (isCategoryFullySelected(catId)) {
      onChange(selectedIds.filter(id => !catItemIds.includes(id)));
    } else {
      const newIds = new Set([...selectedIds, ...catItemIds]);
      onChange(Array.from(newIds));
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {selectedItems.slice(0, 10).map(item => (
            <Badge key={item.id} variant="secondary" className="text-xs gap-1">
              {item.name}
              <button 
                type="button"
                onClick={() => onChange(selectedIds.filter(id => id !== item.id))}
                className="ml-1"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
          {selectedItems.length > 10 && (
            <Badge variant="outline" className="text-xs">
              +{selectedItems.length - 10} more
            </Badge>
          )}
        </div>
      )}
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={placeholder}
          className="pl-8 h-8 text-sm"
        />
      </div>
      <ScrollArea className="h-48 border rounded-md p-2">
        <div className="space-y-1">
          {filteredCategories.map((category) => {
            const catItems = itemsByCategory.get(category.id) || [];
            const searchQ = search.toLowerCase();
            const filteredCatItems = search 
              ? catItems.filter(item => item.name.toLowerCase().includes(searchQ) || category.name.toLowerCase().includes(searchQ))
              : catItems;
            const isExpanded = expandedCategories.has(category.id);
            const fullySelected = isCategoryFullySelected(category.id);
            const partiallySelected = isCategoryPartiallySelected(category.id);
            
            return (
              <div key={category.id}>
                <div className="flex items-center gap-1 py-1">
                  <button 
                    type="button" 
                    onClick={() => toggleCategory(category.id)}
                    className="p-0.5"
                  >
                    {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  </button>
                  <Checkbox
                    id={`cat-${label}-${category.id}`}
                    checked={fullySelected}
                    className={partiallySelected ? 'data-[state=unchecked]:bg-primary/30' : ''}
                    onCheckedChange={() => toggleCategorySelection(category.id)}
                  />
                  <Label 
                    htmlFor={`cat-${label}-${category.id}`} 
                    className="text-xs font-semibold cursor-pointer flex-1"
                    onClick={(e) => { e.preventDefault(); toggleCategory(category.id); }}
                  >
                    {category.name} ({catItems.length})
                  </Label>
                </div>
                {isExpanded && (
                  <div className="ml-7 space-y-0.5 pb-1">
                    {filteredCatItems.map((item) => (
                      <div key={item.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`${label}-item-${item.id}`}
                          checked={selectedIds.includes(item.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              onChange([...selectedIds, item.id]);
                            } else {
                              onChange(selectedIds.filter(id => id !== item.id));
                            }
                          }}
                        />
                        <Label htmlFor={`${label}-item-${item.id}`} className="text-xs font-normal cursor-pointer">
                          {item.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {filteredCategories.length === 0 && (
            <p className="text-xs text-muted-foreground py-2 text-center">No items found</p>
          )}
        </div>
      </ScrollArea>
      <p className="text-xs text-muted-foreground">{selectedItems.length} item(s) selected</p>
    </div>
  );
}

export default function AdminCoupons() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [businessId] = useState(DEFAULT_BUSINESS_ID);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [eatzyToken, setEatzyToken] = useState<string | null>(() => {
    return sessionStorage.getItem('adminEatzyToken');
  });
  const [authError, setAuthError] = useState<string | null>(null);
  
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<EatzyCoupon | null>(null);
  const [formData, setFormData] = useState<CouponFormData>(INITIAL_FORM_DATA);
  const [restaurantSearch, setRestaurantSearch] = useState('');

  useEffect(() => {
    if (eatzyToken) {
      sessionStorage.setItem('adminEatzyToken', eatzyToken);
    }
  }, [eatzyToken]);

  useEffect(() => {
    if (!eatzyToken) {
      const storedToken = sessionStorage.getItem('adminEatzyToken');
      if (storedToken) {
        setEatzyToken(storedToken);
      }
    }
  }, []);

  const [lamEmail, setLamEmail] = useState('');
  const [lamPassword, setLamPassword] = useState('');

  const authMutation = useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      const response = await apiRequest('POST', '/api/admin/lam/auth', credentials);
      return response.json() as Promise<LAMAuthResponse>;
    },
    onSuccess: (data) => {
      if (data.success && data.accessToken) {
        setEatzyToken(data.accessToken);
        sessionStorage.setItem('adminEatzyToken', data.accessToken);
        setAuthError(null);
        setLamPassword('');
      } else {
        setAuthError(data.error || 'Authentication failed');
      }
    },
    onError: (error: Error) => {
      setAuthError(error.message);
    },
  });

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (lamEmail && lamPassword) {
      authMutation.mutate({ email: lamEmail, password: lamPassword });
    }
  };

  const handleLogout = () => {
    setEatzyToken(null);
    sessionStorage.removeItem('adminEatzyToken');
  };

  const getAuthToken = () => localStorage.getItem('auth_token');

  const couponFetch = async (url: string) => {
    if (!eatzyToken) throw new Error('Authentication required');
    const authToken = getAuthToken();
    const response = await fetch(url, {
      headers: { 
        'X-Eatzy-Token': eatzyToken,
        ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
      },
      credentials: 'include',
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (response.status === 401 || response.status === 403) {
        sessionStorage.removeItem('adminEatzyToken');
        setEatzyToken(null);
        throw new Error('Session expired. Please sign in again.');
      }
      throw new Error(errorData.error || `Request failed: ${response.status}`);
    }
    return response.json();
  };

  const { data: coupons, isLoading: isLoadingCoupons, error: couponsError } = useQuery<EatzyCoupon[]>({
    queryKey: ['/api/admin/coupons', businessId, eatzyToken],
    queryFn: () => couponFetch(`/api/admin/coupons/${businessId}`),
    enabled: !!eatzyToken,
  });

  const { data: couponConfig } = useQuery<EatzyCouponConfig>({
    queryKey: ['/api/admin/coupons/config', businessId, eatzyToken],
    queryFn: () => couponFetch(`/api/admin/coupons/${businessId}/config`),
    enabled: !!eatzyToken,
  });

  const { data: promos } = useQuery<any[]>({
    queryKey: ['/api/admin/promos', businessId, eatzyToken],
    queryFn: () => couponFetch(`/api/admin/promos/${businessId}`),
    enabled: !!businessId && !!eatzyToken,
  });

  const createPromoForCoupon = async (couponHash: string) => {
    if (!formData.isPromo || !formData.promoTitle || !formData.promoOffer) return;
    
    const authToken = getAuthToken();
    const startTimestamp = formData.validFrom ? Math.floor(new Date(formData.validFrom).getTime() / 1000) : Math.floor(Date.now() / 1000);
    const endTimestamp = formData.validTo ? Math.floor(new Date(formData.validTo + 'T23:59:59').getTime() / 1000) : startTimestamp + 86400 * 30;
    
    const response = await fetch(`/api/admin/promos/${businessId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Eatzy-Token': eatzyToken!,
        ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
      },
      credentials: 'include',
      body: JSON.stringify({
        coupon: couponHash,
        title: formData.promoTitle,
        offer: formData.promoOffer,
        description: formData.promoDescription,
        start: startTimestamp,
        end: endTimestamp,
        group: businessId,
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      toast({ title: 'Failed to create promo', description: errorData.error || 'Unknown error', variant: 'destructive' });
    }
  };

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const authToken = getAuthToken();
      const response = await fetch('/api/admin/coupons', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Eatzy-Token': eatzyToken!,
          ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
        },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create coupon');
      }
      return response.json();
    },
    onSuccess: async (data: any) => {
      if (formData.isPromo && data?.hash) {
        await createPromoForCoupon(data.hash);
        queryClient.invalidateQueries({ queryKey: ['/api/admin/promos', businessId] });
      }
      queryClient.invalidateQueries({ queryKey: ['/api/admin/coupons', businessId] });
      setIsFormOpen(false);
      setEditingCoupon(null);
      setFormData(INITIAL_FORM_DATA);
      toast({ title: 'Coupon created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error creating coupon', description: error.message, variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const authToken = getAuthToken();
      const response = await fetch(`/api/admin/coupons/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Eatzy-Token': eatzyToken!,
          ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
        },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update coupon');
      }
      return response.json();
    },
    onSuccess: async () => {
      if (editingCoupon) {
        const existingPromo = promos?.find((p: any) => p.coupon === editingCoupon.hash);
        
        if (formData.isPromo && !existingPromo && editingCoupon.hash) {
          await createPromoForCoupon(editingCoupon.hash);
        } else if (!formData.isPromo && existingPromo) {
          try {
            const authToken = getAuthToken();
            await fetch(`/api/admin/promos/${businessId}/${existingPromo.id}`, {
              method: 'DELETE',
              headers: {
                'X-Eatzy-Token': eatzyToken!,
                ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
              },
              credentials: 'include',
            });
          } catch (e) {
            toast({ title: 'Failed to remove promo', variant: 'destructive' });
          }
        } else if (formData.isPromo && existingPromo) {
          try {
            const authToken = getAuthToken();
            await fetch(`/api/admin/promos/${businessId}/${existingPromo.id}`, {
              method: 'DELETE',
              headers: {
                'X-Eatzy-Token': eatzyToken!,
                ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
              },
              credentials: 'include',
            });
            await createPromoForCoupon(editingCoupon.hash);
          } catch (e) {
            toast({ title: 'Failed to update promo', variant: 'destructive' });
          }
        }
        queryClient.invalidateQueries({ queryKey: ['/api/admin/promos', businessId] });
      }
      queryClient.invalidateQueries({ queryKey: ['/api/admin/coupons', businessId] });
      setIsFormOpen(false);
      setEditingCoupon(null);
      setFormData(INITIAL_FORM_DATA);
      toast({ title: 'Coupon updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error updating coupon', description: error.message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const authToken = getAuthToken();
      const response = await fetch(`/api/admin/coupons/${id}`, {
        method: 'DELETE',
        headers: { 
          'X-Eatzy-Token': eatzyToken!,
          ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
        },
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete coupon');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/coupons', businessId] });
      toast({ title: 'Coupon deleted successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error deleting coupon', description: error.message, variant: 'destructive' });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, active }: { id: number; active: boolean }) => {
      const authToken = getAuthToken();
      const response = await fetch(`/api/admin/coupons/${id}/toggle`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-Eatzy-Token': eatzyToken!,
          ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
        },
        credentials: 'include',
        body: JSON.stringify({ active }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to toggle coupon');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/coupons', businessId] });
      toast({ title: 'Coupon status updated' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error updating coupon', description: error.message, variant: 'destructive' });
    },
  });

  const handleOpenCreate = () => {
    setEditingCoupon(null);
    const today = new Date();
    const nextWeek = addDays(today, 7);
    setFormData({
      ...INITIAL_FORM_DATA,
      validFrom: format(today, 'yyyy-MM-dd'),
      validTo: format(nextWeek, 'yyyy-MM-dd'),
    });
    setRestaurantSearch('');
    setIsFormOpen(true);
  };

  const handleOpenEdit = (coupon: EatzyCoupon) => {
    setEditingCoupon(coupon);
    
    const effect = coupon.effects[0] as any;
    const validityRule = coupon.rules.find(r => r.type === 'validity' || r.type === 'period') as any;
    const minOrderRule = coupon.rules.find(r => r.type === 'minorderamount') as any;
    const serviceTypesRule = coupon.rules.find(r => r.type === 'servicetypes') as any;
    const weekdaysRule = coupon.rules.find(r => r.type === 'weekdays') as any;
    const customerRule = coupon.rules.find(r => r.type === 'customer') as any;
    const restaurantsRule = coupon.rules.find(r => r.type === 'restaurants') as any;
    const oneTimeRule = coupon.rules.find(r => r.type === 'onetime' || r.type === 'one_time') as any;
    const menuRule = coupon.rules.find(r => r.type === 'menu') as any;
    
    const effectValue = effect?.params?.value ?? effect?.amount ?? 10;
    const customerParams = customerRule?.params?.value ?? {};
    
    const validFrom = validityRule?.params?.value?.from;
    const validTo = validityRule?.params?.value?.to;
    const fromDate = validFrom ? new Date(validFrom * 1000).toISOString().split('T')[0] : '';
    const toDate = validTo ? new Date(validTo * 1000).toISOString().split('T')[0] : '';
    
    const timeRangeRule = coupon.rules.find(r => r.type === 'timerange') as any;
    const timeRangeValue = timeRangeRule?.params?.value ?? timeRangeRule;
    const secondsToTime = (s: number | string): string => {
      if (typeof s === 'string') return s;
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    };
    const validFromTime = timeRangeValue?.from !== undefined && timeRangeValue?.from !== null && timeRangeValue?.from !== '' ? secondsToTime(timeRangeValue.from) : '';
    const validToTime = timeRangeValue?.to !== undefined && timeRangeValue?.to !== null && timeRangeValue?.to !== '' ? secondsToTime(timeRangeValue.to) : '';
    
    const rulesObj = coupon.rules as any;
    const isObjectRules = rulesObj && !Array.isArray(rulesObj);
    const singleUseFromObject = isObjectRules && (rulesObj.one_time === true);
    const customerFromObject = isObjectRules ? rulesObj.customer : null;
    
    const freeItemEffects = coupon.effects.filter(e => e.type === 'freeitem');
    const freeItemIds = freeItemEffects.map(e => e.params?.value ?? e.amount ?? 0).filter(Boolean);
    
    const menuRuleValue = menuRule?.params?.value;
    const requiredItemIds = menuRuleValue?.identifiers ?? [];
    const requiredItemMinimum = menuRuleValue?.minimum ?? 1;
    
    let effectType: CouponFormData['effectType'] = 'percentage';
    if (freeItemEffects.length > 0) {
      effectType = 'freeitems';
    } else if (effect?.type === 'freedelivery') {
      effectType = 'freedelivery';
    } else if (effect?.type === 'flat') {
      effectType = 'flat';
    } else if (effect?.type === 'percentage') {
      effectType = 'percentage';
    }
    
    const existingPromo = promos?.find((p: any) => p.coupon === coupon.hash);
    
    setFormData({
      code: coupon.code,
      name: coupon.name,
      active: coupon.active,
      effectType,
      effectAmount: effectType === 'percentage' ? effectValue : (effectType === 'flat' ? effectValue : 10),
      freeItemIds,
      validFrom: fromDate,
      validTo: toDate,
      validFromTime,
      validToTime,
      minOrderAmount: minOrderRule?.params?.value ?? minOrderRule?.amount ?? 0,
      serviceTypes: serviceTypesRule?.params?.value ?? serviceTypesRule?.servicetypes ?? [],
      weekdays: weekdaysRule?.params?.value ?? weekdaysRule?.weekdays ?? [0, 1, 2, 3, 4, 5, 6],
      singleUse: oneTimeRule !== undefined || singleUseFromObject,
      oncePerCustomer: customerParams.one_time_use ?? customerFromObject?.one_time_use ?? false,
      newCustomersOnly: customerParams.only_new_customers ?? customerParams.new_only ?? customerFromObject?.only_new_customers ?? customerFromObject?.new_only ?? false,
      selectedRestaurants: restaurantsRule?.params?.value ?? restaurantsRule?.restaurants ?? [],
      requiredItemIds,
      requiredItemMinimum,
      isPromo: !!existingPromo,
      promoTitle: existingPromo?.title ?? '',
      promoOffer: existingPromo?.offer ?? '',
      promoDescription: existingPromo?.description ?? '',
    });
    setRestaurantSearch('');
    setIsFormOpen(true);
  };

  const handleSubmit = () => {
    const effects: Record<string, any> = {};
    if (formData.effectType === 'freedelivery') {
      effects.free_delivery = true;
    } else if (formData.effectType === 'flat') {
      effects.flat_discount = formData.effectAmount;
    } else if (formData.effectType === 'percentage') {
      effects.percentage_discount = formData.effectAmount / 100;
    } else if (formData.effectType === 'freeitems') {
      if (formData.freeItemIds.length > 0) {
        effects.free_items = formData.freeItemIds;
      }
    }
    
    const rules: Record<string, any> = {};
    
    if (formData.validFrom && formData.validTo) {
      const fromDate = new Date(formData.validFrom);
      const toDate = new Date(formData.validTo);
      rules.validity = { 
        from: Math.floor(fromDate.getTime() / 1000), 
        to: Math.floor(toDate.getTime() / 1000) 
      };
    }
    
    const fromTime = formData.validFromTime && formData.validFromTime !== '__clear__' ? formData.validFromTime : '';
    const toTime = formData.validToTime && formData.validToTime !== '__clear__' ? formData.validToTime : '';
    if (fromTime && toTime) {
      const timeToSeconds = (t: string) => {
        const [h, m] = t.split(':').map(Number);
        return h * 3600 + m * 60;
      };
      rules.time_range = {
        from: timeToSeconds(fromTime),
        to: timeToSeconds(toTime),
      };
    }
    
    if (formData.serviceTypes.length > 0) {
      rules.service_types = formData.serviceTypes;
    }
    
    if (formData.minOrderAmount > 0) {
      rules.min_order_amount = formData.minOrderAmount;
    }
    
    if (formData.effectType === 'flat' && formData.minOrderAmount <= 0) {
      rules.min_order_amount = formData.effectAmount;
    }
    
    if (formData.weekdays.length > 0 && formData.weekdays.length < 7) {
      rules.week_days = formData.weekdays;
    }
    
    if (formData.singleUse) {
      rules.one_time = true;
    }
    
    if (formData.oncePerCustomer || formData.newCustomersOnly) {
      rules.customer = { 
        one_time_use: formData.oncePerCustomer ?? false, 
        only_new_customers: formData.newCustomersOnly ?? false 
      };
    }
    
    if (formData.selectedRestaurants.length > 0) {
      rules.restaurants = formData.selectedRestaurants;
    }
    
    if (formData.requiredItemIds.length > 0) {
      rules.menu = {
        items: formData.requiredItemIds,
        minimum: formData.requiredItemMinimum,
      };
    }
    
    const baseCouponData = {
      business: businessId,
      code: formData.code,
      name: formData.name,
      owner: 'business' as const,
      effects,
      rules,
    };
    
    console.log('[AdminCoupons] Submitting coupon data:', JSON.stringify(baseCouponData, null, 2));
    
    if (editingCoupon) {
      updateMutation.mutate({ id: editingCoupon.id, data: { ...baseCouponData, active: formData.active } });
    } else {
      createMutation.mutate(baseCouponData);
    }
  };

  const [showExpired, setShowExpired] = useState(false);

  const isCouponExpired = (coupon: EatzyCoupon) => {
    const validityRule = coupon.rules.find(r => r.type === 'validity' || r.type === 'period') as any;
    const endTimestamp = validityRule?.params?.value?.to;
    if (!endTimestamp) return false;
    return endTimestamp * 1000 < Date.now();
  };

  const filteredCoupons = coupons?.filter(c => {
    if (!showExpired && isCouponExpired(c)) return false;
    return c.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.name.toLowerCase().includes(searchQuery.toLowerCase());
  }) ?? [];

  const filteredRestaurants = useMemo(() => {
    if (!couponConfig?.restaurants) return [];
    if (!restaurantSearch) return couponConfig.restaurants;
    const q = restaurantSearch.toLowerCase();
    return couponConfig.restaurants.filter(r => r.name.toLowerCase().includes(q));
  }, [couponConfig?.restaurants, restaurantSearch]);

  if (!eatzyToken) {
    return (
      <AdminLayout>
        <div className="max-w-md mx-auto mt-12">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <KeyRound className="w-5 h-5" />
                Eatzy Authentication
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Sign in with your Eatzy credentials to manage coupons for Business ID {businessId}.
              </p>
              
              {authError && (
                <div className="flex items-center gap-2 p-3 mb-4 bg-destructive/10 text-destructive rounded-md">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">{authError}</span>
                </div>
              )}
              
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={lamEmail}
                    onChange={(e) => setLamEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    data-testid="input-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={lamPassword}
                    onChange={(e) => setLamPassword(e.target.value)}
                    placeholder="Password"
                    required
                    data-testid="input-password"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full"
                  disabled={authMutation.isPending}
                  data-testid="button-login"
                >
                  {authMutation.isPending ? (
                    'Authenticating...'
                  ) : (
                    <>
                      <LogIn className="w-4 h-4 mr-2" />
                      Sign In
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Ticket className="w-6 h-6" />
              Coupon Management
            </h1>
            <p className="text-muted-foreground">
              Business ID: {businessId} | {coupons?.length ?? 0} coupons
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-800 rounded-md dark:bg-green-900/30 dark:text-green-400">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-sm">Authenticated</span>
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search coupons..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="input-search"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Switch
              id="showExpired"
              checked={showExpired}
              onCheckedChange={setShowExpired}
              data-testid="toggle-show-expired"
            />
            <Label htmlFor="showExpired" className="text-sm text-muted-foreground whitespace-nowrap cursor-pointer">
              Show expired
            </Label>
          </div>

          <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
            <DialogTrigger asChild>
              <Button onClick={handleOpenCreate} data-testid="button-create-coupon">
                <Plus className="w-4 h-4 mr-2" />
                Create Coupon
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90svh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="code">Coupon Code</Label>
                    <Input
                      id="code"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                      placeholder="SAVE10"
                      maxLength={32}
                      data-testid="input-coupon-code"
                    />
                    <p className="text-xs text-muted-foreground">Letters and numbers only, max 32 characters</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="name">Name / Description</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="10% off all orders"
                      data-testid="input-coupon-name"
                    />
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold mb-4 text-muted-foreground uppercase tracking-wide">Conditions</h4>
                  <div className="space-y-6">

                    {couponConfig && couponConfig.restaurants.length > 0 && (
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Store className="w-4 h-4" />
                          Apply to Restaurants
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Select restaurants this coupon applies to. To use item-based conditions or free items, select exactly one restaurant.
                        </p>
                        <div className="relative">
                          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                          <Input
                            value={restaurantSearch}
                            onChange={(e) => setRestaurantSearch(e.target.value)}
                            placeholder="Search restaurants..."
                            className="pl-8 h-8 text-sm mb-2"
                          />
                        </div>
                        <ScrollArea className="h-32 border rounded-md p-2">
                          <div className="space-y-2">
                            {filteredRestaurants.map((restaurant) => (
                              <div key={restaurant.id} className="flex items-center gap-2">
                                <Checkbox
                                  id={`restaurant-${restaurant.id}`}
                                  checked={formData.selectedRestaurants.includes(restaurant.id)}
                                  onCheckedChange={(checked) => {
                                    const newRestaurants = checked
                                      ? [...formData.selectedRestaurants, restaurant.id]
                                      : formData.selectedRestaurants.filter(r => r !== restaurant.id);
                                    const newData = { ...formData, selectedRestaurants: newRestaurants };
                                    if (newRestaurants.length !== 1) {
                                      newData.freeItemIds = [];
                                      newData.requiredItemIds = [];
                                    }
                                    setFormData(newData);
                                  }}
                                />
                                <Label htmlFor={`restaurant-${restaurant.id}`} className="text-sm font-normal">
                                  {restaurant.name}
                                </Label>
                              </div>
                            ))}
                            {filteredRestaurants.length === 0 && (
                              <p className="text-xs text-muted-foreground py-2 text-center">No restaurants found</p>
                            )}
                          </div>
                        </ScrollArea>
                        {formData.selectedRestaurants.length > 0 && (
                          <p className="text-xs text-muted-foreground">{formData.selectedRestaurants.length} restaurant(s) selected</p>
                        )}
                        <p className="text-xs text-muted-foreground">Leave empty for all restaurants in business</p>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Valid From</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal",
                                !formData.validFrom && "text-muted-foreground"
                              )}
                              data-testid="input-valid-from"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {formData.validFrom ? format(new Date(formData.validFrom + 'T00:00:00'), 'PPP') : 'Pick a date'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <CalendarComponent
                              mode="single"
                              selected={formData.validFrom ? new Date(formData.validFrom + 'T00:00:00') : undefined}
                              onSelect={(date) => setFormData({ ...formData, validFrom: date ? format(date, 'yyyy-MM-dd') : '' })}
                              today={new Date()}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="space-y-2">
                        <Label>Valid Until</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal",
                                !formData.validTo && "text-muted-foreground"
                              )}
                              data-testid="input-valid-to"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {formData.validTo ? format(new Date(formData.validTo + 'T00:00:00'), 'PPP') : 'Pick a date'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <CalendarComponent
                              mode="single"
                              selected={formData.validTo ? new Date(formData.validTo + 'T00:00:00') : undefined}
                              onSelect={(date) => setFormData({ ...formData, validTo: date ? format(date, 'yyyy-MM-dd') : '' })}
                              today={new Date()}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Valid During Specific Hours (Optional)
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Restrict this coupon to specific times of day (e.g., lunch specials from 11 AM to 2 PM)
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Start Time</Label>
                          <Select
                            value={formData.validFromTime}
                            onValueChange={(val) => setFormData({ ...formData, validFromTime: val })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Any time" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__clear__">Any time (no restriction)</SelectItem>
                              {TIME_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">End Time</Label>
                          <Select
                            value={formData.validToTime}
                            onValueChange={(val) => setFormData({ ...formData, validToTime: val })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Any time" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__clear__">Any time (no restriction)</SelectItem>
                              {TIME_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {formData.validFromTime && formData.validToTime && formData.validFromTime !== '__clear__' && formData.validToTime !== '__clear__' && (
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            <Clock className="w-3 h-3 mr-1" />
                            Active: {TIME_OPTIONS.find(t => t.value === formData.validFromTime)?.label} - {TIME_OPTIONS.find(t => t.value === formData.validToTime)?.label}
                          </Badge>
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setFormData({ ...formData, validFromTime: '', validToTime: '' })}
                          >
                            Clear
                          </Button>
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label>Valid Days</Label>
                      <div className="flex flex-wrap gap-2">
                        {WEEKDAY_NAMES.map((day, index) => (
                          <Button
                            key={day}
                            type="button"
                            variant={formData.weekdays.includes(index) ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => {
                              const newDays = formData.weekdays.includes(index)
                                ? formData.weekdays.filter(d => d !== index)
                                : [...formData.weekdays, index].sort();
                              setFormData({ ...formData, weekdays: newDays });
                            }}
                          >
                            {day}
                          </Button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Service Types</Label>
                      <div className="flex flex-wrap gap-2">
                        {SERVICE_TYPES.map((type) => (
                          <Button
                            key={type}
                            type="button"
                            variant={formData.serviceTypes.includes(type) ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => {
                              const newTypes = formData.serviceTypes.includes(type)
                                ? formData.serviceTypes.filter(t => t !== type)
                                : [...formData.serviceTypes, type];
                              setFormData({ ...formData, serviceTypes: newTypes });
                            }}
                          >
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </Button>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">Leave empty for all service types</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="minOrderAmount">Minimum Order Amount ($)</Label>
                        <Input
                          id="minOrderAmount"
                          type="number"
                          min={0}
                          value={formData.minOrderAmount}
                          onChange={(e) => setFormData({ ...formData, minOrderAmount: parseFloat(e.target.value) || 0 })}
                          data-testid="input-min-order"
                        />
                      </div>
                    </div>

                    <div className="space-y-3">
                      <Label className="flex items-center gap-2">
                        <ShoppingCart className="w-4 h-4" />
                        Required Items in Cart (Optional)
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Require the customer to have specific items in their cart for this coupon to apply.
                        For example: "Must order 2 Pizzas to get free delivery" or "Order any appetizer to get $5 off."
                      </p>
                      {formData.selectedRestaurants.length !== 1 ? (
                        <div className="rounded-md border border-dashed p-4 text-center">
                          <ShoppingCart className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">
                            Select exactly one restaurant above to choose required items from its menu.
                          </p>
                        </div>
                      ) : couponConfig?.items && couponConfig?.categories ? (
                        <>
                          <CategoryItemPicker
                            items={couponConfig.items}
                            categories={couponConfig.categories}
                            selectedIds={formData.requiredItemIds}
                            onChange={(ids) => setFormData({ ...formData, requiredItemIds: ids })}
                            label="Required Items"
                            placeholder="Search for required items..."
                            restaurantId={formData.selectedRestaurants[0]}
                          />
                          {formData.requiredItemIds.length > 0 && (
                            <div className="space-y-2">
                              <Label htmlFor="requiredItemMinimum">How many of the items selected above does the customer need to buy in order to qualify for the coupon?</Label>
                              <Input
                                id="requiredItemMinimum"
                                type="number"
                                min={1}
                                value={formData.requiredItemMinimum}
                                onChange={(e) => setFormData({ ...formData, requiredItemMinimum: parseInt(e.target.value) || 1 })}
                              />
                            </div>
                          )}
                        </>
                      ) : null}
                    </div>

                    <div className="space-y-3">
                      <Label>Usage Restrictions</Label>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="singleUse"
                            checked={formData.singleUse}
                            onCheckedChange={(checked) => setFormData({ ...formData, singleUse: !!checked })}
                            data-testid="checkbox-single-use"
                          />
                          <Label htmlFor="singleUse" className="text-sm font-normal">
                            Single use (coupon invalid after any customer uses it)
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="oncePerCustomer"
                            checked={formData.oncePerCustomer}
                            onCheckedChange={(checked) => setFormData({ ...formData, oncePerCustomer: !!checked })}
                            data-testid="checkbox-once-per-customer"
                          />
                          <Label htmlFor="oncePerCustomer" className="text-sm font-normal">
                            Once per customer (each customer can use once)
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="newCustomersOnly"
                            checked={formData.newCustomersOnly}
                            onCheckedChange={(checked) => setFormData({ ...formData, newCustomersOnly: !!checked })}
                            data-testid="checkbox-new-customers-only"
                          />
                          <Label htmlFor="newCustomersOnly" className="text-sm font-normal">
                            New customers only (first-time customers only)
                          </Label>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold mb-4 text-muted-foreground uppercase tracking-wide">Effects</h4>
                  <div className="space-y-6">

                    <div className="space-y-2">
                      <Label>Effect Type</Label>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <Button
                          type="button"
                          variant={formData.effectType === 'percentage' ? 'default' : 'outline'}
                          onClick={() => setFormData({ ...formData, effectType: 'percentage' })}
                          className="w-full"
                        >
                          <Percent className="w-4 h-4 mr-2" />
                          Percentage
                        </Button>
                        <Button
                          type="button"
                          variant={formData.effectType === 'flat' ? 'default' : 'outline'}
                          onClick={() => setFormData({ ...formData, effectType: 'flat' })}
                          className="w-full"
                        >
                          <DollarSign className="w-4 h-4 mr-2" />
                          Flat Amount
                        </Button>
                        <Button
                          type="button"
                          variant={formData.effectType === 'freedelivery' ? 'default' : 'outline'}
                          onClick={() => setFormData({ ...formData, effectType: 'freedelivery' })}
                          className="w-full"
                        >
                          <Truck className="w-4 h-4 mr-2" />
                          Free Delivery
                        </Button>
                        <Button
                          type="button"
                          variant={formData.effectType === 'freeitems' ? 'default' : 'outline'}
                          onClick={() => setFormData({ ...formData, effectType: 'freeitems' })}
                          className="w-full"
                        >
                          <Gift className="w-4 h-4 mr-2" />
                          Free Items
                        </Button>
                      </div>
                    </div>

                    {formData.effectType === 'percentage' && (
                      <div className="space-y-2">
                        <Label htmlFor="effectAmount">Discount Percentage</Label>
                        <Input
                          id="effectAmount"
                          type="number"
                          min={1}
                          max={100}
                          value={formData.effectAmount}
                          onChange={(e) => setFormData({ ...formData, effectAmount: parseFloat(e.target.value) || 0 })}
                          data-testid="input-effect-amount"
                        />
                        <p className="text-xs text-muted-foreground">Enter 10 for 10% off</p>
                      </div>
                    )}

                    {formData.effectType === 'flat' && (
                      <div className="space-y-2">
                        <Label htmlFor="effectAmount">Discount Amount ($)</Label>
                        <Input
                          id="effectAmount"
                          type="number"
                          min={0}
                          value={formData.effectAmount}
                          onChange={(e) => setFormData({ ...formData, effectAmount: parseFloat(e.target.value) || 0 })}
                          data-testid="input-effect-amount"
                        />
                        <p className="text-xs text-muted-foreground">A minimum order amount is required for flat discounts</p>
                        {formData.minOrderAmount <= 0 && (
                          <p className="text-xs text-orange-600 dark:text-orange-400">
                            Will default to ${formData.effectAmount} minimum order
                          </p>
                        )}
                      </div>
                    )}

                    {formData.effectType === 'freeitems' && (
                      formData.selectedRestaurants.length !== 1 ? (
                        <div className="rounded-md border border-dashed p-4 text-center">
                          <Gift className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">
                            Select exactly one restaurant in the Conditions section above to choose free items.
                          </p>
                        </div>
                      ) : couponConfig?.items && couponConfig?.categories ? (
                        <CategoryItemPicker
                          items={couponConfig.items}
                          categories={couponConfig.categories}
                          selectedIds={formData.freeItemIds}
                          onChange={(ids) => setFormData({ ...formData, freeItemIds: ids })}
                          label="Free Items (what the customer gets)"
                          placeholder="Search items to give free..."
                          restaurantId={formData.selectedRestaurants[0]}
                        />
                      ) : null
                    )}

                  </div>
                </div>
                
                {/* Section 5: Promo (Optional) */}
                <div className="border-t pt-4 space-y-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold">5</span>
                    <h4 className="text-sm font-semibold">Feature as a promo? (Optional)</h4>
                  </div>

                  <div className="rounded-md border p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="promoToggle" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                          <Megaphone className="w-4 h-4" />
                          Offer this coupon as a Promo
                        </Label>
                        <p className="text-xs text-muted-foreground mt-1">
                          Promos can be featured in the app on restaurant pages and deals listings
                        </p>
                      </div>
                      <Switch
                        id="promoToggle"
                        checked={formData.isPromo}
                        onCheckedChange={(checked) => {
                          setFormData({ ...formData, isPromo: checked, ...(!checked ? { promoTitle: '', promoOffer: '', promoDescription: '' } : {}) });
                        }}
                      />
                    </div>
                    {formData.isPromo && (
                      <div className="pt-2 space-y-3">
                        <div className="space-y-2">
                          <Label htmlFor="promoTitle">Title</Label>
                          <Input
                            id="promoTitle"
                            value={formData.promoTitle}
                            onChange={(e) => setFormData({ ...formData, promoTitle: e.target.value })}
                            placeholder='e.g. "Taco Tuesday"'
                            data-testid="input-promo-title"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="promoOffer">Offer</Label>
                          <Input
                            id="promoOffer"
                            value={formData.promoOffer}
                            onChange={(e) => setFormData({ ...formData, promoOffer: e.target.value })}
                            placeholder='e.g. "Get 2 Free Tacos"'
                            data-testid="input-promo-offer"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="promoDescription">Description</Label>
                          <Input
                            id="promoDescription"
                            value={formData.promoDescription}
                            onChange={(e) => setFormData({ ...formData, promoDescription: e.target.value })}
                            placeholder='e.g. "Orders over $50 on Tuesday will get 2 free Street Tacos"'
                            data-testid="input-promo-description"
                          />
                        </div>
                        <div className="rounded-md bg-muted/50 p-2">
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Sparkles className="w-3 h-3" />
                            The promo dates will match the coupon's start and end dates above.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    id="active"
                    checked={formData.active}
                    onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                  />
                  <Label htmlFor="active">Active</Label>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsFormOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSubmit}
                  disabled={createMutation.isPending || updateMutation.isPending || !formData.code || !formData.name}
                  data-testid="button-submit-coupon"
                >
                  {(createMutation.isPending || updateMutation.isPending) ? 'Saving...' : (editingCoupon ? 'Update Coupon' : 'Create Coupon')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {isLoadingCoupons ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <Skeleton className="h-6 w-32 mb-2" />
                  <Skeleton className="h-4 w-48 mb-4" />
                  <Skeleton className="h-8 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : couponsError ? (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="w-5 h-5" />
                <span>Error loading coupons: {couponsError.message}</span>
              </div>
            </CardContent>
          </Card>
        ) : filteredCoupons.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Ticket className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">No coupons found</h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery ? 'Try a different search term' : 'Create your first coupon to get started'}
              </p>
              {!searchQuery && (
                <Button onClick={handleOpenCreate}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Coupon
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredCoupons.map((coupon) => (
              <Card key={coupon.id} className="relative" data-testid={`coupon-card-${coupon.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <CardTitle className="text-lg font-mono">{coupon.code}</CardTitle>
                      <p className="text-sm text-muted-foreground">{coupon.name}</p>
                    </div>
                    {promos?.find((p: any) => p.coupon === coupon.hash) && (
                      <Badge variant="outline" className="text-xs flex items-center gap-1">
                        <Megaphone className="w-3 h-3" />
                        Promo
                      </Badge>
                    )}
                    <Badge variant={coupon.active ? 'default' : 'secondary'}>
                      {coupon.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex flex-wrap gap-1">
                    {coupon.effects.map((effect, i) => (
                      <Badge key={i} variant="outline" className="text-xs">
                        {effect.type === 'flat' && <DollarSign className="w-3 h-3 mr-1" />}
                        {effect.type === 'percentage' && <Percent className="w-3 h-3 mr-1" />}
                        {effect.type === 'freedelivery' && <Truck className="w-3 h-3 mr-1" />}
                        {effect.type === 'freeitem' && <Gift className="w-3 h-3 mr-1" />}
                        {getEffectLabel(effect)}
                      </Badge>
                    ))}
                  </div>
                  
                  {coupon.rules.length > 0 && (
                    <div className="text-xs text-muted-foreground space-y-1">
                      {coupon.rules.slice(0, 3).map((rule, i) => (
                        <div key={i} className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {getRuleLabel(rule)}
                        </div>
                      ))}
                      {coupon.rules.length > 3 && (
                        <span className="text-muted-foreground">+{coupon.rules.length - 3} more rules</span>
                      )}
                    </div>
                  )}
                  
                  {coupon.uses !== undefined && (
                    <p className="text-xs text-muted-foreground">
                      Used {coupon.uses} times
                    </p>
                  )}
                  
                  <div className="flex items-center gap-2 pt-2 border-t">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleMutation.mutate({ id: coupon.id, active: !coupon.active })}
                      disabled={toggleMutation.isPending}
                      data-testid={`button-toggle-${coupon.id}`}
                    >
                      {coupon.active ? (
                        <ToggleRight className="w-4 h-4 mr-1 text-green-600" />
                      ) : (
                        <ToggleLeft className="w-4 h-4 mr-1" />
                      )}
                      {coupon.active ? 'Deactivate' : 'Activate'}
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenEdit(coupon)}
                      data-testid={`button-edit-${coupon.id}`}
                    >
                      <Pencil className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-destructive"
                          data-testid={`button-delete-${coupon.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Coupon</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete coupon "{coupon.code}"? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => deleteMutation.mutate(coupon.id)}
                            className="bg-destructive text-destructive-foreground"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
